import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";

const { Text } = Typography;

const EMPTY_SUMMARY = {
  total: 0,
  autoMatched: 0,
  alreadyLinked: 0,
  manualReview: 0,
  unmatched: 0,
  conflicts: 0,
};

const STATUS_META = {
  AUTO_MATCHED: { label: "Matched", color: "green" },
  MANUALLY_MATCHED: { label: "Matched manually", color: "blue" },
  MANUAL_REVIEW: { label: "Conflict / review", color: "orange" },
  UNMATCHED: { label: "Failed to match", color: "red" },
  CONFLICT: { label: "Conflict", color: "magenta" },
};

const STATUS_FILTERS = {
  ALL: null,
  LINKED: ["AUTO_MATCHED", "MANUALLY_MATCHED"],
  REVIEW: ["MANUAL_REVIEW"],
  UNMATCHED: ["UNMATCHED"],
  CONFLICT: ["CONFLICT"],
};

const sourceStudentName = (record) =>
  [
    record?.sourceStudent?.firstName,
    record?.sourceStudent?.middleName,
    record?.sourceStudent?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

const SurePayStudentMatchingModal = ({ visible, onCancel, uploadCSV }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    if (!visible) {
      setFileList([]);
      setRecords([]);
      setSummary(EMPTY_SUMMARY);
      setStatusFilter("ALL");
    }
  }, [visible]);

  const handleUpload = async () => {
    const selectedFile = fileList[0]?.originFileObj || fileList[0];
    if (!selectedFile) return;
    setUploading(true);
    try {
      const result = await uploadCSV(selectedFile);
      if (result) {
        setRecords(result.records || []);
        setStatusFilter("ALL");
        setSummary({
          ...EMPTY_SUMMARY,
          total: result.total || 0,
          autoMatched: result.autoMatched || 0,
          alreadyLinked: result.alreadyLinked || 0,
          manualReview: result.manualReview || 0,
          unmatched: result.unmatched || 0,
          conflicts: result.conflicts || 0,
        });
        setFileList([]);
      }
    } finally {
      setUploading(false);
    }
  };

  const statusCounts = records.reduce(
    (counts, record) => {
      counts.ALL += 1;
      if (STATUS_FILTERS.LINKED.includes(record.status)) counts.LINKED += 1;
      if (record.status === "MANUAL_REVIEW") counts.REVIEW += 1;
      if (record.status === "UNMATCHED") counts.UNMATCHED += 1;
      if (record.status === "CONFLICT") counts.CONFLICT += 1;
      return counts;
    },
    { ALL: 0, LINKED: 0, REVIEW: 0, UNMATCHED: 0, CONFLICT: 0 },
  );

  const selectedStatuses = STATUS_FILTERS[statusFilter];
  const filteredRecords = selectedStatuses
    ? records.filter((record) => selectedStatuses.includes(record.status))
    : records;

  const statusFilterOptions = [
    { value: "ALL", label: `All records (${statusCounts.ALL})` },
    { value: "LINKED", label: `Linked (${statusCounts.LINKED})` },
    { value: "REVIEW", label: `Needs review (${statusCounts.REVIEW})` },
    { value: "UNMATCHED", label: `Unmatched (${statusCounts.UNMATCHED})` },
    { value: "CONFLICT", label: `Conflicts (${statusCounts.CONFLICT})` },
  ];

  const columns = [
    {
      title: "SurePay code",
      dataIndex: "surePayStudentCode",
      width: 170,
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Text copyable>{value}</Text>
          {record.sourceRowNumber && (
            <Text type="secondary">CSV row {record.sourceRowNumber}</Text>
          )}
        </Space>
      ),
    },
    {
      title: "SurePay student",
      key: "sourceStudent",
      width: 250,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{sourceStudentName(record) || "-"}</Text>
          <Space size={4} wrap>
            <Tag>{record.sourceStudent?.gender || "-"}</Tag>
            <Tag color="geekblue">{record.sourceStudent?.className || "-"}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: "Result",
      dataIndex: "status",
      width: 165,
      render: (value) => {
        const meta = STATUS_META[value] || { label: value, color: "default" };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Edunex student",
      key: "matchedStudent",
      render: (_, record) => {
        const student = record.matchedStudent;
        return student ? (
          <Space direction="vertical" size={0}>
            <Text strong>{student.name}</Text>
            <Text type="secondary">
              {[student.className, student.streamName, student.spc]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">No student matched</Text>
        );
      },
    },
    {
      title: "Details",
      key: "matchReasons",
      width: 330,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {(record.matchReasons || []).map((reason) => (
            <Text type="secondary" key={reason}>
              {reason}
            </Text>
          ))}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Sync SurePay"
      open={visible}
      onCancel={onCancel}
      width={1250}
      footer={<Button onClick={onCancel}>Close</Button>}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card size="small" title="">
          <Space wrap>
            <Upload
              accept=".csv,text/csv"
              beforeUpload={() => false}
              fileList={fileList}
              maxCount={1}
              onChange={({ fileList: nextFiles }) =>
                setFileList(nextFiles.slice(-1))
              }
              onRemove={() => setFileList([])}
            >
              <Button icon={<UploadOutlined />}>Choose CSV</Button>
            </Upload>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              disabled={!fileList.length}
              loading={uploading}
              onClick={handleUpload}
            >
              Upload and match
            </Button>
          </Space>
        </Card>

        {records.length > 0 && (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic title="Processed" value={summary.total} />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic
                    title="Matched"
                    value={summary.autoMatched + summary.alreadyLinked}
                    valueStyle={{ color: "#389e0d" }}
                  />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic
                    title="Review"
                    value={summary.manualReview}
                    valueStyle={{ color: "#d48806" }}
                  />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic
                    title="Failed"
                    value={summary.unmatched}
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card size="small">
                  <Statistic
                    title="Conflicts"
                    value={summary.conflicts}
                    valueStyle={{ color: "#c41d7f" }}
                  />
                </Card>
              </Col>
            </Row>
            <Space wrap>
              <Text strong>Status</Text>
              <Select
                aria-label="Filter SurePay records by status"
                value={statusFilter}
                options={statusFilterOptions}
                onChange={setStatusFilter}
                style={{ minWidth: 210 }}
              />
              <Text type="secondary">
                Showing {filteredRecords.length} of {records.length} records
              </Text>
            </Space>
            <Table
              key={statusFilter}
              rowKey="importId"
              columns={columns}
              dataSource={filteredRecords}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 1100 }}
            />
          </>
        )}
      </Space>
    </Modal>
  );
};

export default SurePayStudentMatchingModal;
